import { Injectable, computed, signal } from '@angular/core';
import {
    PROVIDER_LEVEL_RANK,
    ProviderComponentStatus,
    ProviderConfig,
    ProviderEvent,
    ProviderEventPhase,
    ProviderLevel,
    ProviderStatus,
} from '../models/provider-status.model';
import { UnitStatusProvidersService } from './unit-status-providers.service';

/** Resolved incidents older than this are left out of the history. */
const HISTORY_DAYS = 7;
/** Some status pages (e.g. Frisbii) regularly take ~10 s to answer. */
const REQUEST_TIMEOUT_MS = 20_000;
/** Background refresh while the app is open (drives the sidebar badge too). */
const POLL_INTERVAL_MS = 10 * 60 * 1000;

// --- Minimal shapes of the public APIs (only the fields we read) ---

interface SpUpdate { body: string; status: string; created_at: string; }
interface SpComponent { id: string; name: string; status: string; group?: boolean; group_id?: string | null; }
interface SpIncident {
    id: string;
    name: string;
    status: string;
    impact: string;
    created_at: string;
    updated_at: string | null;
    resolved_at: string | null;
    scheduled_for?: string | null;
    scheduled_until?: string | null;
    shortlink: string;
    incident_updates: SpUpdate[];
    components?: SpComponent[];
}
interface SpSummary {
    status: { indicator: string; description: string };
    components: SpComponent[];
    incidents: SpIncident[];
    scheduled_maintenances: SpIncident[];
}

interface BsIncluded { id: string; type: string; attributes: Record<string, any>; relationships?: Record<string, any>; }
interface BsIndex {
    data: { attributes: { aggregate_state: string } };
    included: BsIncluded[];
}

/**
 * Live status of the third-party providers the current unit uses (see resolveStatusProviders).
 * Reads each provider's public status page directly from the browser (no PubQ backend involved).
 * Ported from skynet-web's ProviderStatusService. Data is fetched when the app loads a unit,
 * every 10 minutes while the tab is visible (started by the sidebar), and on Refresh.
 */
@Injectable({ providedIn: 'root' })
export class ProviderStatusService {
    public readonly statuses = signal<ProviderStatus[]>([]);
    public readonly isLoading = signal<boolean>(false);
    public readonly lastUpdated = signal<Date | null>(null);
    /** Route the statuses belong to; '' until the first load. */
    public readonly routeName = signal<string>('');

    /** Every provider's events in display order: ongoing, then upcoming, then recently resolved. */
    public readonly events = computed(() => sortEvents(this.statuses().flatMap((s) => s.events)));

    /**
     * Worst level among the providers that have loaded (unreachable ones count as operational),
     * or null before the first one has — drives the dot on the sidebar's Status item.
     */
    public readonly overallLevel = computed<ProviderLevel | null>(() => {
        const loaded = this.statuses().filter((s) => !s.loading);
        return loaded.length ? worstLevel(loaded.map((s) => s.level)) : null;
    });

    /** Bumped on every load so a slow answer for a previous unit can't overwrite the current one. */
    private generation = 0;
    /** Country of the loaded unit, reused by the background refresh. */
    private countryCode = '';
    private pollTimer: ReturnType<typeof setInterval> | null = null;

    constructor(private unitStatusProviders: UnitStatusProvidersService) {}

    /**
     * Refreshes the loaded unit every 10 minutes. Skipped while the tab is hidden; when it
     * becomes visible again after a missed refresh, it refreshes right away. No-op if running.
     */
    startPolling(): void {
        if (this.pollTimer) return;
        this.pollTimer = setInterval(() => {
            if (!document.hidden) this.refreshInBackground();
        }, POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    stopPolling(): void {
        if (this.pollTimer) clearInterval(this.pollTimer);
        this.pollTimer = null;
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    private readonly onVisibilityChange = () => {
        const last = this.lastUpdated();
        if (!document.hidden && last && Date.now() - last.getTime() >= POLL_INTERVAL_MS) {
            this.refreshInBackground();
        }
    };

    private refreshInBackground(): void {
        if (this.isLoading() || !this.routeName()) return;
        void this.loadForUnit(this.routeName(), this.countryCode, { force: true });
    }

    /**
     * Loads the statuses for a unit. Without `force` this is a no-op when that unit is already
     * loaded (or loading), so the sidebar and the Status page can both call it on load.
     * `countryCode` is the unit's salesLocationCountryCode (scopes Vipps MobilePay).
     */
    async loadForUnit(routeName: string, countryCode: string | null | undefined, options: { force?: boolean } = {}): Promise<void> {
        if (!routeName) return;
        if (!options.force && this.routeName() === routeName) return;

        const generation = ++this.generation;
        const routeChanged = this.routeName() !== routeName;
        this.routeName.set(routeName);
        this.countryCode = countryCode || '';
        this.isLoading.set(true);
        if (routeChanged) {
            this.statuses.set([]);
            this.lastUpdated.set(null);
        }

        try {
            const providers = await this.unitStatusProviders.getProviders(routeName, countryCode);
            if (generation !== this.generation) return;

            // Keep the previous data of a provider visible until its new response arrives.
            const previous = new Map(this.statuses().map((s) => [s.id, s]));
            this.statuses.set(providers.map((p) => previous.get(p.id) ?? emptyStatus(p, { loading: true })));

            await Promise.all(
                providers.map(async (provider) => {
                    const status = await this.fetchProvider(provider);
                    if (generation !== this.generation) return;
                    this.statuses.update((list) => list.map((s) => (s.id === provider.id ? status : s)));
                }),
            );
            if (generation === this.generation) this.lastUpdated.set(new Date());
        } finally {
            if (generation === this.generation) this.isLoading.set(false);
        }
    }

    private async fetchProvider(provider: ProviderConfig): Promise<ProviderStatus> {
        try {
            const status = provider.source === 'statuspage'
                ? await this.fetchStatuspage(provider)
                : await this.fetchBetterStack(provider);
            const hide = provider.hideEventsMatching;
            return hide ? { ...status, events: status.events.filter((e) => !hide.test(e.title)) } : status;
        } catch (err) {
            console.error(`[ProviderStatusService] Could not fetch ${provider.name}:`, err);
            return emptyStatus(provider, { error: err instanceof Error ? err.message : String(err) });
        }
    }

    // --- Atlassian Statuspage ---

    private async fetchStatuspage(provider: ProviderConfig): Promise<ProviderStatus> {
        const [summary, history] = await Promise.all([
            getJson<SpSummary>(`${provider.apiBaseUrl}/api/v2/summary.json`),
            // History is a nice-to-have: if it fails, still show the current status.
            getJson<{ incidents: SpIncident[] }>(`${provider.apiBaseUrl}/api/v2/incidents.json`).catch((err) => {
                console.warn(`[ProviderStatusService] No incident history for ${provider.name}:`, err);
                return { incidents: [] as SpIncident[] };
            }),
        ]);

        // Optional scoping to some component groups (e.g. only the Finland group of Vipps MobilePay).
        const scope = provider.componentGroups
            ? new Set(summary.components.filter((c) => c.group && provider.componentGroups!.includes(c.name)).map((c) => c.id))
            : null;
        const inScope = (c: SpComponent) => !c.group && (!scope || (!!c.group_id && scope.has(c.group_id)));
        // Incidents without any components can't be attributed to a group, so they're kept.
        const isRelevant = (i: SpIncident) => !scope || !i.components?.length || i.components.some(inScope);

        const affectedComponents: ProviderComponentStatus[] = summary.components
            .filter(inScope)
            .map((c) => ({ name: c.name, level: statuspageComponentLevel(c.status) }))
            .filter((c) => c.level !== 'operational');

        const events: ProviderEvent[] = [];
        for (const incident of summary.incidents.filter(isRelevant)) {
            events.push(statuspageEvent(provider, incident, 'incident', 'active', inScope));
        }
        for (const maintenance of summary.scheduled_maintenances.filter(isRelevant)) {
            const phase: ProviderEventPhase = maintenance.status === 'scheduled' ? 'scheduled' : 'active';
            events.push(statuspageEvent(provider, maintenance, 'maintenance', phase, inScope));
        }

        const cutoff = historyCutoff();
        const seen = new Set([...summary.incidents, ...summary.scheduled_maintenances].map((i) => i.id));
        for (const incident of history.incidents.filter(isRelevant)) {
            const resolvedAt = parseDate(incident.resolved_at);
            if (seen.has(incident.id) || !resolvedAt || resolvedAt < cutoff) continue;
            events.push(statuspageEvent(provider, incident, 'incident', 'resolved', inScope));
        }

        // The page-wide indicator covers every group, so when scoped, derive the level from what's in scope.
        const level = scope
            ? worstLevel([
                ...affectedComponents.map((c) => c.level),
                ...events.filter((e) => e.phase === 'active').map((e) => e.level),
            ])
            : worstLevel([statuspageIndicatorLevel(summary.status.indicator), ...affectedComponents.map((c) => c.level)]);

        return {
            id: provider.id,
            name: provider.name,
            pageUrl: provider.pageUrl,
            level,
            description: scope ? '' : summary.status.description,
            affectedComponents,
            events,
            error: null,
            loading: false,
        };
    }

    // --- Better Stack ---

    private async fetchBetterStack(provider: ProviderConfig): Promise<ProviderStatus> {
        const index = await getJson<BsIndex>(`${provider.apiBaseUrl}/index.json`);
        const byType = (type: string) => index.included.filter((i) => i.type === type);

        const resources = byType('status_page_resource');
        const resourceNames = new Map(resources.map((r) => [r.id, String(r.attributes['public_name'] ?? '')]));
        const updates = new Map(byType('status_update').map((u) => [u.id, u.attributes]));

        const affectedComponents: ProviderComponentStatus[] = resources
            .map((r) => ({ name: String(r.attributes['public_name'] ?? ''), level: betterStackLevel(r.attributes['status']) }))
            .filter((c) => c.level !== 'operational');

        const now = new Date();
        const cutoff = historyCutoff();
        const events: ProviderEvent[] = [];
        for (const report of byType('status_report')) {
            const a = report.attributes;
            const startsAt = parseDate(a['starts_at']);
            const endsAt = parseDate(a['ends_at']);
            const isMaintenance = a['report_type'] === 'maintenance';

            let phase: ProviderEventPhase;
            if (a['aggregate_state'] === 'resolved' || (endsAt && endsAt <= now)) phase = 'resolved';
            else if (startsAt && startsAt > now) phase = 'scheduled';
            else phase = 'active';
            if (phase === 'resolved' && (endsAt ?? startsAt ?? now) < cutoff) continue;

            const reportUpdates = ((report.relationships?.['status_updates']?.data ?? []) as { id: string }[])
                .map((ref) => updates.get(ref.id))
                .filter((u): u is Record<string, any> => !!u)
                .sort((x, y) => (parseDate(y['published_at'])?.getTime() ?? 0) - (parseDate(x['published_at'])?.getTime() ?? 0));
            const latest = reportUpdates[0];

            events.push({
                id: `${provider.id}-${report.id}`,
                providerId: provider.id,
                providerName: provider.name,
                kind: isMaintenance ? 'maintenance' : 'incident',
                phase,
                level: isMaintenance ? 'maintenance' : betterStackLevel(a['aggregate_state']),
                title: String(a['title'] ?? ''),
                message: plainText(latest?.['message'] ?? ''),
                startsAt,
                endsAt,
                updatedAt: parseDate(latest?.['published_at']),
                affected: ((a['affected_resources'] ?? []) as { status_page_resource_id: string }[])
                    .map((r) => resourceNames.get(String(r.status_page_resource_id)) ?? '')
                    .filter(Boolean),
                link: provider.pageUrl,
            });
        }

        const pageLevel = betterStackLevel(index.data.attributes.aggregate_state);
        return {
            id: provider.id,
            name: provider.name,
            pageUrl: provider.pageUrl,
            level: worstLevel([pageLevel, ...affectedComponents.map((c) => c.level)]),
            description: '',
            affectedComponents,
            events,
            error: null,
            loading: false,
        };
    }
}

function emptyStatus(
    provider: ProviderConfig,
    overrides: Partial<Pick<ProviderStatus, 'error' | 'loading'>>,
): ProviderStatus {
    return {
        id: provider.id,
        name: provider.name,
        pageUrl: provider.pageUrl,
        level: 'unknown',
        description: '',
        affectedComponents: [],
        events: [],
        error: null,
        loading: false,
        ...overrides,
    };
}

// --- Helpers ---

async function getJson<T>(url: string): Promise<T> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status} from ${url}`);
        return (await res.json()) as T;
    } finally {
        clearTimeout(timer);
    }
}

function statuspageEvent(
    provider: ProviderConfig,
    incident: SpIncident,
    kind: 'incident' | 'maintenance',
    phase: ProviderEventPhase,
    inScope: (c: SpComponent) => boolean,
): ProviderEvent {
    const latest = incident.incident_updates?.[0];
    const isMaintenance = kind === 'maintenance';
    return {
        id: `${provider.id}-${incident.id}`,
        providerId: provider.id,
        providerName: provider.name,
        kind,
        phase,
        level: isMaintenance ? 'maintenance' : statuspageImpactLevel(incident.impact),
        title: incident.name,
        message: plainText(latest?.body ?? ''),
        startsAt: parseDate(isMaintenance ? incident.scheduled_for : incident.created_at),
        endsAt: parseDate(isMaintenance ? incident.scheduled_until : incident.resolved_at),
        updatedAt: parseDate(latest?.created_at ?? incident.updated_at),
        affected: [...new Set((incident.components ?? []).filter(inScope).map((c) => c.name))],
        link: incident.shortlink || provider.pageUrl,
    };
}

function statuspageIndicatorLevel(indicator: string): ProviderLevel {
    switch (indicator) {
        case 'none': return 'operational';
        case 'maintenance': return 'maintenance';
        case 'minor': return 'degraded';
        case 'major': return 'partial_outage';
        case 'critical': return 'major_outage';
        default: return 'unknown';
    }
}

function statuspageImpactLevel(impact: string): ProviderLevel {
    return impact === 'none' ? 'degraded' : statuspageIndicatorLevel(impact);
}

function statuspageComponentLevel(status: string): ProviderLevel {
    switch (status) {
        case 'operational': return 'operational';
        case 'under_maintenance': return 'maintenance';
        case 'degraded_performance': return 'degraded';
        case 'partial_outage': return 'partial_outage';
        case 'major_outage': return 'major_outage';
        default: return 'unknown';
    }
}

function betterStackLevel(state: unknown): ProviderLevel {
    switch (state) {
        case 'operational':
        case 'resolved': return 'operational';
        case 'maintenance': return 'maintenance';
        case 'degraded': return 'degraded';
        case 'downtime': return 'major_outage';
        default: return 'unknown';
    }
}

function worstLevel(levels: ProviderLevel[]): ProviderLevel {
    return levels.reduce<ProviderLevel>(
        (worst, l) => (PROVIDER_LEVEL_RANK[l] > PROVIDER_LEVEL_RANK[worst] ? l : worst),
        'operational',
    );
}

function sortEvents(events: ProviderEvent[]): ProviderEvent[] {
    const phaseOrder: Record<ProviderEventPhase, number> = { active: 0, scheduled: 1, resolved: 2 };
    const time = (d: Date | null) => d?.getTime() ?? 0;
    return [...events].sort((a, b) => {
        if (a.phase !== b.phase) return phaseOrder[a.phase] - phaseOrder[b.phase];
        if (a.phase === 'active') return PROVIDER_LEVEL_RANK[b.level] - PROVIDER_LEVEL_RANK[a.level] || time(b.startsAt) - time(a.startsAt);
        if (a.phase === 'scheduled') return time(a.startsAt) - time(b.startsAt); // soonest first
        return time(b.endsAt ?? b.updatedAt) - time(a.endsAt ?? a.updatedAt); // most recent first
    });
}

function historyCutoff(): Date {
    return new Date(Date.now() - HISTORY_DAYS * 24 * 60 * 60 * 1000);
}

function parseDate(value: unknown): Date | null {
    if (!value) return null;
    const date = new Date(String(value));
    return isNaN(date.getTime()) ? null : date;
}

/** Provider messages may contain HTML; show them as plain text. */
function plainText(value: string): string {
    if (!value) return '';
    const doc = new DOMParser().parseFromString(value, 'text/html');
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}
